/**
 * Shared helpers for per-row recurrence on DSNY maintenance schedule rows.
 *
 * Only custom ("+Add another schedule line") rows and Hallway Cleaning may carry a
 * recurrence type other than weekly. DSNY pickup rows (garbage/recycling/organics/bulk)
 * are always weekly because NYC collection is on fixed weekdays.
 */

export type DsnyRowRecurrenceType = 'daily' | 'weekly' | 'bi_weekly' | 'monthly'
export type DsnyMonthlyMode = 'nth_weekday' | 'day_of_month'

export interface DsnyRowRecurrence {
  type: string // row.type
  combine_with_dsny?: boolean
  /** When set, monthly rows can piggyback onto DSNY or hallway weekly visits. */
  combine_target?: 'dsny' | 'hallway' | null
  recurrence_type?: DsnyRowRecurrenceType | null
  monthly_mode?: DsnyMonthlyMode | null
  recurrence_weekday?: number | null
  recurrence_occurrence?: number | null
  recurrence_day_of_month?: number | null
  snap_to_closest_visit?: boolean | null
}

/** Rows that are allowed to override recurrence type. */
export function rowSupportsRecurrence(rowType: string): boolean {
  return rowType === 'custom' || rowType === 'hallway_cleaning'
}

/** Effective recurrence type for a row (pickup rows are always weekly). */
export function getRowRecurrenceType(row: DsnyRowRecurrence): DsnyRowRecurrenceType {
  if (!rowSupportsRecurrence(row.type)) return 'weekly'
  return row.recurrence_type || 'weekly'
}

export function isRowWeekly(row: DsnyRowRecurrence): boolean {
  return getRowRecurrenceType(row) === 'weekly'
}

function rowCombinesWithVisitGroup(row: DsnyRowRecurrence): boolean {
  return (
    row.combine_target === 'dsny' ||
    row.combine_target === 'hallway' ||
    !!row.combine_with_dsny
  )
}

/**
 * A monthly day-of-month row that should ride an existing weekly visit
 * (DSNY exterior or hallway) rather than create its own visit group.
 */
export function isSnapToClosestRow(row: DsnyRowRecurrence): boolean {
  return (
    getRowRecurrenceType(row) === 'monthly' &&
    row.monthly_mode === 'day_of_month' &&
    !!row.snap_to_closest_visit &&
    rowCombinesWithVisitGroup(row)
  )
}

/**
 * Non-weekly rows that should attach to a DSNY/hallway weekly group as piggybacks
 * (monthly nth-weekday, day-of-month, bi-weekly combine, etc.).
 */
export function isPiggybackCombineRow(row: DsnyRowRecurrence): boolean {
  if (!rowCombinesWithVisitGroup(row)) return false
  const rtype = getRowRecurrenceType(row)
  if (rtype === 'weekly') return false
  return true
}

/**
 * Stable signature used to bucket rows into visit groups. Weekly rows share a
 * signature so they can still merge by day; non-weekly rows get a distinct one.
 */
export function rowRecurrenceSignature(row: DsnyRowRecurrence): string {
  const rtype = getRowRecurrenceType(row)
  if (rtype === 'weekly') return 'weekly'
  if (rtype === 'daily') return 'daily'
  if (rtype === 'bi_weekly') return 'bi_weekly'
  // monthly
  if (row.monthly_mode === 'nth_weekday') {
    return `monthly:nth:${row.recurrence_occurrence ?? 1}:${row.recurrence_weekday ?? 0}`
  }
  if (row.monthly_mode === 'day_of_month') {
    return `monthly:dom:${row.recurrence_day_of_month ?? 1}:${row.snap_to_closest_visit ? 'snap' : 'exact'}`
  }
  return 'monthly:anchor'
}

/**
 * Recurrence fields to persist on a contract_service / visit group / mapping for a row.
 */
export function buildRowRecurrenceFields(row: DsnyRowRecurrence): {
  recurrence_type: DsnyRowRecurrenceType
  recurrence_weekday: number | null
  recurrence_occurrence: number | null
  recurrence_day_of_month: number | null
  recurrence_nearest_visit: boolean
} {
  const recurrence_type = getRowRecurrenceType(row)
  if (recurrence_type !== 'monthly') {
    return {
      recurrence_type,
      recurrence_weekday: null,
      recurrence_occurrence: null,
      recurrence_day_of_month: null,
      recurrence_nearest_visit: false,
    }
  }
  if (row.monthly_mode === 'day_of_month') {
    return {
      recurrence_type,
      recurrence_weekday: null,
      recurrence_occurrence: null,
      recurrence_day_of_month: row.recurrence_day_of_month ?? 1,
      recurrence_nearest_visit: !!row.snap_to_closest_visit,
    }
  }
  // nth weekday — UI selects default to 1st Monday visually; persist those defaults
  // when state still has nulls so piggybacks are not saved with an empty cadence.
  return {
    recurrence_type,
    recurrence_weekday: row.recurrence_weekday ?? 1,
    recurrence_occurrence: row.recurrence_occurrence ?? 1,
    recurrence_day_of_month: null,
    recurrence_nearest_visit: false,
  }
}

export const OCCURRENCE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: 'Last' },
]

export const RECURRENCE_TYPE_OPTIONS: Array<{ value: DsnyRowRecurrenceType; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

export const WEEKDAY_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]
